import type { TicketFeeMode, TicketVatTreatment } from '@pulsify/shared'
import type { TicketSettings } from '@pulsify/shared/dto'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { feeRuleLabel, planName } from '~/entities/subscription/plans'
import { useUpdateTicketSettings } from '~/entities/ticket/model/use-tickets'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { InvoiceIssuerDialog } from './invoice-issuer-dialog'
import { PayoutSetupButton } from './payout-setup'
import { VatSelect } from './vat-select'

function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5 py-3 first:pt-0 last:pb-0">
      <p className="font-medium text-sm">{title}</p>
      {children}
    </div>
  )
}

export function TicketSettingsPanel({
  businessId,
  hostProfileId,
  settings,
}: {
  businessId: string
  hostProfileId?: string
  settings: TicketSettings
}) {
  const update = useUpdateTicketSettings(businessId, hostProfileId)
  const [issuerOpen, setIssuerOpen] = useState(false)
  const { connect, invoice } = settings
  const feeMode = (update.isPending && update.variables?.feeMode) || settings.feeMode
  const vatTreatment =
    update.isPending && update.variables?.vatTreatment !== undefined
      ? update.variables.vatTreatment
      : invoice.vatTreatment

  const save = (
    change: { feeMode?: TicketFeeMode; vatTreatment?: TicketVatTreatment | null },
    done: string,
  ) =>
    update.mutate(change, {
      onSuccess: () => toast.success(done),
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })

  const choose = (next: TicketFeeMode) => {
    if (next !== feeMode) save({ feeMode: next }, m.ticketsFeeSaved())
  }

  const seller = invoice.issuer
  const sellerPlace = seller ? [seller.postalCode, seller.city].filter(Boolean).join(' ') : ''

  return (
    <div className="divide-y divide-border">
      <Row title={m.ticketsFeeTitle()}>
        <p className="text-muted-foreground text-sm">
          {m.ticketsFeeLine({ fee: feeRuleLabel(settings.fee), plan: planName(settings.tier) })}
        </p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
          <span className="text-sm">{m.ticketsFeePaidBy()}</span>
          <SegmentedControl
            size="sm"
            label={m.ticketsFeePaidBy()}
            value={feeMode}
            options={[
              { value: 'buyer', label: m.ticketsFeeBuyer() },
              { value: 'organizer', label: m.ticketsFeeOrganizer() },
            ]}
            onChange={choose}
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {feeMode === 'buyer' ? m.ticketsFeeBuyerHint() : m.ticketsFeeOrganizerHint()}
        </p>
      </Row>
      <Row title={m.ticketsPayoutsTitle()}>
        {connect.onboardingComplete ? (
          <>
            <p className="text-muted-foreground text-sm">
              {connect.payoutsEnabled ? m.ticketsPayoutsReady() : m.ticketsPayoutsChecking()}
            </p>
            {hostProfileId ? (
              connect.own ? (
                <p className="text-muted-foreground text-sm">{m.ticketsPayoutsOwn()}</p>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <p className="min-w-0 flex-1 text-muted-foreground text-sm">
                    {m.ticketsPayoutsShared()}
                  </p>
                  <PayoutSetupButton
                    businessId={businessId}
                    hostProfileId={hostProfileId}
                    started={false}
                    label={m.ticketsPayoutsOwnStart()}
                    variant="outline"
                  />
                </div>
              )
            ) : null}
          </>
        ) : connect.available ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-muted-foreground text-sm">
              {connect.accountId ? m.ticketsPayoutsUnfinished() : m.ticketsPayoutsNotSet()}
            </p>
            <PayoutSetupButton
              businessId={businessId}
              hostProfileId={hostProfileId}
              started={connect.accountId !== null}
              variant="outline"
            />
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{m.ticketsPaidSoon()}</p>
        )}
      </Row>
      <Row title={m.ticketsInvoiceTitle()}>
        <p className="text-muted-foreground text-sm">{m.ticketsInvoiceLine()}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
          <span className="text-sm">{m.ticketsInvoiceVat()}</span>
          <VatSelect
            value={vatTreatment}
            placeholder={m.ticketsInvoiceVatUnset()}
            onChange={(next) => save({ vatTreatment: next }, m.ticketsInvoiceSaved())}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <div className="min-w-0 flex-1 text-sm">
            <p>{m.ticketsInvoiceSeller()}</p>
            <p className="text-muted-foreground">
              {seller
                ? [seller.legalName, seller.street, sellerPlace, seller.vatId]
                    .filter(Boolean)
                    .join(', ')
                : m.ticketsInvoiceSellerNone()}
            </p>
            {invoice.ownIssuer ? (
              <p className="text-muted-foreground">{m.ticketsInvoiceSellerOwn()}</p>
            ) : null}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setIssuerOpen(true)}>
            {m.ticketsInvoiceEdit()}
          </Button>
        </div>
        {invoice.ready ? null : (
          <p className="rounded-lg bg-muted px-3 py-2.5 text-sm">{m.ticketsInvoiceMissing()}</p>
        )}
      </Row>
      <InvoiceIssuerDialog
        open={issuerOpen}
        onOpenChange={setIssuerOpen}
        businessId={businessId}
        hostProfileId={hostProfileId}
        settings={settings}
      />
    </div>
  )
}
